import type { HelpTopic as ApiHelpTopic, Me } from '@myrewards/contracts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { aiKeys, aiStatusQuery, helpTopicsQuery, searchHelp, sendHelpHandoff } from '../api/ai';
import type { AppShellProps } from '../shell';
import type { HelpSource, HelpTopic, PageHelp } from '../shell/types';
import { aiProblemWords } from './aiWords';
import { HelpChat } from './HelpChat';
import { helpPageKey } from './pageKey';

const NO_TOPICS: readonly HelpTopic[] = [];

function toShellTopic(topic: ApiHelpTopic): HelpTopic {
  return {
    id: topic.id,
    category: topic.category,
    title: topic.title,
    summary: topic.summary,
    body: topic.body,
    ...(topic.keywords !== undefined && { keywords: topic.keywords }),
    ...(topic.related !== undefined && { related: topic.related }),
    ...(topic.steps !== undefined && { steps: topic.steps }),
    ...(topic.start !== undefined && { start: topic.start }),
    ...(topic.show !== undefined && { show: topic.show }),
  };
}

type HelpWiring = Pick<
  AppShellProps,
  'aiHelpEnabled' | 'helpTopics' | 'helpSource' | 'helpChat' | 'helpContact' | 'onHelpOpen'
>;

/**
 * Everything the shell's Help panel needs from the server (APP-048): which features Admin › AI
 * turned on for me (`/v1/ai/status`), the topics for this page, search, the Stock Admin team, and
 * the chat. Nothing loads until Help is first opened; with the chat off nothing goes to a model.
 */
export function useHelpWiring(me: Me, page: PageHelp): HelpWiring {
  const queryClient = useQueryClient();
  const { pathname, search: query, key: navigation } = useLocation();
  const [wanted, setWanted] = useState(false);
  const pageKey = helpPageKey(pathname, page.pageKey, query);
  const status = useQuery({ ...aiStatusQuery, enabled: wanted });
  const topics = useQuery({
    ...helpTopicsQuery(pageKey),
    enabled: wanted,
  });
  const data = topics.isError || topics.isFetching ? undefined : topics.data;
  const helpTopics = useMemo(() => data?.topics.map(toShellTopic) ?? NO_TOPICS, [data]);
  const aiHelpEnabled = status.data?.helpChat === true && topics.data?.chat === true;

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: aiKeys.status });
    void queryClient.invalidateQueries({ queryKey: aiKeys.help });
  }, [queryClient]);

  // A search belongs to a navigation and publication snapshot, even when the page key is reused.
  // Retained query data stays stable during its own mandatory refresh; changed content renews it.
  const searchContext = useMemo(
    () => ({ pageKey, navigation, snapshot: topics.data }),
    [pageKey, navigation, topics.data],
  );
  const search = useCallback(
    async (q: string, { signal }: { signal: AbortSignal }) => {
      await queryClient.query(helpTopicsQuery(searchContext.pageKey));
      return (await searchHelp(q, signal)).topics.map(toShellTopic);
    },
    [queryClient, searchContext],
  );
  useEffect(() => {
    if (wanted) void queryClient.invalidateQueries({ queryKey: aiKeys.topics(pageKey) });
  }, [wanted, pathname, query, pageKey, queryClient]);

  const helpSource = useMemo<HelpSource>(
    () => ({
      status: topics.isError ? 'failed' : data !== undefined ? 'ready' : 'loading',
      ...(me.roles.some((r) => r.role === 'stock_admin' || r.role === 'system_admin') && {
        manageHref: '/settings/help-catalog',
      }),
      ...(data !== undefined && {
        popularIds: data.popularIds,
        pageTopicIds: data.page.topicIds,
        pageAnchors: data.page.anchors ?? [],
      }),
      search,
      onRetry: () => {
        void topics.refetch();
      },
    }),
    [data, topics, search, me.roles],
  );

  const contact = data?.contact ?? null;
  const helpContact = useMemo(
    () =>
      contact === null
        ? undefined
        : {
            title: contact.title,
            body: contact.body,
            team: contact.team.map((p) => p.name),
            email: contact.email,
            ...(contact.canWrite && {
              onSend: async (message: { subject: string; message: string }) => {
                try {
                  const sent = await sendHelpHandoff({ ...message, conversationId: null });
                  return sent.to.map((p) => p.name);
                } catch (error) {
                  throw new Error(aiProblemWords(error, 'help'), { cause: error });
                }
              },
            }),
          },
    [contact],
  );

  const remainingToday = status.data?.remainingToday ?? null;
  const voiceOffered = status.data?.helpVoice === true;
  const voiceSecondsLeft = status.data?.voiceSecondsLeft ?? null;
  const suggestions = data?.suggestions;
  const helpChat = useMemo<AppShellProps['helpChat']>(
    () =>
      aiHelpEnabled
        ? ({ openTopic, showMe }) => (
            <HelpChat
              pageKey={pageKey}
              greetingName={me.person.greetingName}
              initials={me.person.initials}
              suggestions={suggestions ?? []}
              topics={helpTopics}
              contact={contact}
              remainingToday={remainingToday}
              openTopic={(id) => {
                // Historical citations resolve against a fresh publication/access snapshot.
                void queryClient.query(helpTopicsQuery(pageKey)).then(
                  () => {
                    openTopic(id);
                  },
                  () => {
                    openTopic(id);
                  },
                );
              }}
              {...(showMe !== undefined && { showMe })}
              onSwitchedOff={refresh}
              voiceOffered={voiceOffered}
              voiceSecondsLeft={voiceSecondsLeft}
            />
          )
        : undefined,
    [
      aiHelpEnabled,
      pageKey,
      me.person.greetingName,
      me.person.initials,
      suggestions,
      helpTopics,
      contact,
      remainingToday,
      voiceOffered,
      voiceSecondsLeft,
      refresh,
      queryClient,
    ],
  );

  const onHelpOpen = useCallback(() => {
    setWanted(true);
    // Admin › AI may have changed since the last look: check again each time Help opens.
    if (wanted) {
      void queryClient.invalidateQueries({ queryKey: aiKeys.status });
      void queryClient.invalidateQueries({ queryKey: aiKeys.help });
    }
  }, [queryClient, wanted]);

  return {
    aiHelpEnabled,
    helpTopics,
    helpSource,
    ...(helpChat !== undefined && { helpChat }),
    ...(helpContact !== undefined && { helpContact }),
    onHelpOpen,
  };
}
